import type { ScheduleCoverageRow } from "./schedule";
import { weekDays } from "./schedule-builder";

// Copy-week preview model (CONSOLE-SCHEDULE-COPY-WEEK-PREVIEW-SCAFFOLD). A PURE comparison
// of the previous week's slots against the visible target week, built from existing
// schedule_coverage rows only - this module mutates nothing and the surface that renders it
// is zero-rpc pinned. Verdict semantics anticipate an idempotent backend copy: "copy" would
// create a slot, "exists" would be skipped (same post + target day + window already there),
// "skipped" covers cancelled/draft sources that a copy must not resurrect. At-risk sources
// (decline signals) still copy but carry the caveat so managers see it before applying.

export type CopyVerdict = "copy" | "exists" | "skipped";

export interface CopyPreviewRow {
  siteName: string;
  routeName: string;
  /** Source date + 7 days - where the copied slot would land. */
  targetDate: string;
  window: string;
  headcountRequired: number;
  credentials: readonly string[];
  verdict: CopyVerdict;
  skippedReason: "cancelled" | "draft" | null;
  atRiskSource: boolean;
}

export interface CopyWeekPreview {
  rows: CopyPreviewRow[];
  summary: { copyCount: number; existsCount: number; skippedCount: number };
}

function addDaysIso(dateIso: string, days: number): string {
  const [year, month, day] = dateIso.split("-").map(Number);
  const date = new Date(Date.UTC(year!, month! - 1, day! + days));
  return date.toISOString().slice(0, 10);
}

function clock(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone }).format(
    new Date(iso),
  );
}

function dayInZone(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", timeZone }).format(
    new Date(iso),
  );
}

function windowLabel(row: ScheduleCoverageRow, timeZone: string): string {
  if (row.startsAt === null || row.endsAt === null) {
    return "time not set";
  }
  const crosses = dayInZone(row.startsAt, timeZone) !== dayInZone(row.endsAt, timeZone);
  return `${clock(row.startsAt, timeZone)} -> ${clock(row.endsAt, timeZone)}${crosses ? " +1" : ""}`;
}

function matchKey(siteName: string | null, routeName: string | null, date: string, window: string): string {
  return `${siteName ?? ""}::${routeName ?? ""}::${date}::${window}`;
}

export function buildCopyWeekPreview(
  sourceRows: readonly ScheduleCoverageRow[],
  targetRows: readonly ScheduleCoverageRow[],
  sourceWeekStart: string,
  targetWeekStart: string,
  timeZone: string,
): CopyWeekPreview {
  const sourceDays = new Set(weekDays(sourceWeekStart));
  const targetKeys = new Set(
    targetRows
      .filter((row) => row.slotDate !== null && row.slotStatus !== "cancelled")
      .map((row) => matchKey(row.siteName, row.routeName, row.slotDate!, windowLabel(row, timeZone))),
  );

  const rows: CopyPreviewRow[] = [];
  let copyCount = 0;
  let existsCount = 0;
  let skippedCount = 0;

  for (const source of sourceRows) {
    if (source.slotDate === null || !sourceDays.has(source.slotDate)) {
      continue; // outside the source week - dropped, never misplaced
    }
    const targetDate = addDaysIso(source.slotDate, 7);
    const window = windowLabel(source, timeZone);
    let verdict: CopyVerdict = "copy";
    let skippedReason: CopyPreviewRow["skippedReason"] = null;
    if (source.slotStatus === "cancelled" || source.slotStatus === "draft") {
      verdict = "skipped";
      skippedReason = source.slotStatus;
      skippedCount += 1;
    } else if (targetKeys.has(matchKey(source.siteName, source.routeName, targetDate, window))) {
      verdict = "exists";
      existsCount += 1;
    } else {
      copyCount += 1;
    }
    rows.push({
      siteName: source.siteName ?? "Site",
      routeName: source.routeName ?? "Route",
      targetDate,
      window,
      headcountRequired: source.headcountRequired,
      credentials: source.requiredCredentialTypes,
      verdict,
      skippedReason,
      atRiskSource: source.declinedCount > 0,
    });
  }

  rows.sort((a, b) =>
    `${a.siteName}${a.routeName}${a.targetDate}${a.window}`.localeCompare(
      `${b.siteName}${b.routeName}${b.targetDate}${b.window}`,
    ),
  );
  return { rows, summary: { copyCount, existsCount, skippedCount } };
}
